import {
  Component,
  EventEmitter,
  Input,
  NO_ERRORS_SCHEMA,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {ErrorMessageComponent} from '../../v1/error-component/error-message.component';
import {CommonModule} from '@angular/common';
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';
import {GateweeIconComponent} from '../../v2/gatewee-icon/gatewee-icon.component';
import {TranslatePipe} from '@ngx-translate/core';
import {GateweeSkeletonComponent} from '../../v1/gatewee-skeleton/gatewee-skeleton.component';

@Component({
  selector: 'gatewee-input',
  templateUrl: './gatewee-input.component.html',
  styleUrls: ['./gatewee-input.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    NgxMaskDirective,
    GateweeIconComponent,
    TranslatePipe,
    GateweeSkeletonComponent,
  ],
  providers: [provideNgxMask()],
  schemas: [NO_ERRORS_SCHEMA],
})
export class GateweeInputComponent implements OnInit {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() form!: FormGroup | any;
  @Input() controlName!: string;
  @Input() label!: string;
  @Input() placeholder = '';
  @Input() id!: string;
  @Input() type: 'text' | 'number' | 'currency' | 'rate' | 'email' = 'text';
  @Input() submitted = false;
  @Input() customMessages: Record<string, string> = {};
  @Input() mask = '';
  @Input() required = false;
  @Input() format: 'currency' | 'percent' | null = 'currency';
  @Input() max!: number | string;
  @Input() min!: number | string;
  @Input() hint!: {message: string; icon: string};
  @Input() info!: string;
  @Input() isLoading = false;
  @Input() switch = false;
  @Input() iconName = '';
  @Input() readonly = false;
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() currencyChange = new EventEmitter<boolean>();

  isCurrency = true;

  get control(): FormControl {
    const ctrl = this.form.get(this.controlName);
    if (!ctrl) {
      throw new Error(`Control "${this.controlName}" não existe`);
    }
    return ctrl as FormControl;
  }

  ngOnInit(): void {
    if (this.min) {
      const ctrl = this.control;
      const validators = ctrl.validator ? [ctrl.validator] : [];
      ctrl.setValidators([
        ...validators,
        Validators.minLength(Number(this.min)),
      ]);
      ctrl.updateValueAndValidity();
    }
  }

  get displayValue(): string {
    if (this.type === 'currency') {
      const value = this.control.value;
      return this.formatCurrency(value);
    }

    return this.control.value ?? '';
  }

  get hasValidation(): boolean {
    const ctrl = this.control;
    return !!ctrl.validator || !!ctrl.asyncValidator;
  }

  get inputClasses(): Record<string, boolean> {
    return {
      normal: this.control.dirty || !!this.control.value,
      'is-invalid':
        this.hasValidation && this.control.touched && this.control.invalid,
      'is-disabled': this.control.disabled,
    };
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (this.type === 'currency') {
      const numericValue = this.parseCurrency(value);
      this.control.setValue(numericValue);
    } else {
      this.control.setValue(value);
    }
  }

  toggleCurrency() {
    this.isCurrency = !this.isCurrency;

    this.currencyChange.emit(this.isCurrency);
  }

  onBlur(event: FocusEvent): void {
    this.blur.emit(event);

    this.control.markAsTouched();

    if (this.type === 'number' && this.format === 'currency') {
      const value = this.control.value;
      this.control.setValue(this.parseCurrency(this.formatCurrency(value)));
    }
  }

  private formatCurrency(value: number): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  private parseCurrency(value: string): number {
    if (!value) return 0;
    const numeric = value.replace(/[^\d,]/g, '').replace(',', '.');
    return parseFloat(numeric);
  }
}
